import React, { useState, useEffect } from 'react';
import {
  Card,
  Statistic,
  Table,
  Tabs,
  Form,
  Input,
  Switch,
  Button,
  Alert,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Divider,
  message,
  Tooltip,
  Layout,
  Progress,
  Spin,
  DatePicker,
  Modal
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  BellOutlined,
  LinkOutlined,
  MailOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  RiseOutlined,
  FallOutlined,
  LineChartOutlined,
  CalendarOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/Header';
import { billingAPI } from '../services/api';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface CreditBalance {
  current_balance: number;
  total_purchased: number;
  total_consumed: number;
  credits_consumed_today: number;
  credits_consumed_month: number;
  thresholds_triggered: number[];
  last_topup_date?: string;
  plan?: CreditPlan;
}

interface CreditPlan {
  plan_id: string;
  plan_code: string;
  name: string;
  default_credits: number;
  price_per_unit: number;
  currency: string;
  description: string;
  features: string[];
  is_active: boolean;
}

interface Transaction {
  transaction_id: string;
  type: 'purchase' | 'deduction' | 'consumption' | 'adjustment';
  credits: number;
  balance_after: number;
  description: string;
  created_at: string;
  metadata?: any;
}

interface AlertPreferences {
  email_alerts_enabled: boolean;
  threshold_75_enabled: boolean;
  threshold_50_enabled: boolean;
  threshold_25_enabled: boolean;
  threshold_5_enabled: boolean;
  threshold_0_enabled: boolean;
}

interface UsageDataPoint {
  date: string;
  consumed: number;
  balance: number;
  purchased: number;
}

const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [plan, setPlan] = useState<CreditPlan | null>(null);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [alertPreferences, setAlertPreferences] = useState<AlertPreferences>({
    email_alerts_enabled: true,
    threshold_75_enabled: true,
    threshold_50_enabled: true,
    threshold_25_enabled: true,
    threshold_5_enabled: true,
    threshold_0_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [usageData, setUsageData] = useState<UsageDataPoint[]>([]);
  const [forecastDays, setForecastDays] = useState(30);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportDateRange, setExportDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);

      // Single optimized API call to get all billing data
      const response = await billingAPI.getBillingOverview({ limit: 50 });
      const data = response.data?.data || response.data || {};

      // Set all state from single response with null checks
      setBalance(data.balance || null);
      setTransactions(data.transactions || []);
      setPlan(data.plan || data.balance?.plan || null);
      setWebhookEnabled(data.webhook_enabled || false);
      setAlertPreferences(data.alert_preferences || {
        email_alerts_enabled: true,
        threshold_75_enabled: true,
        threshold_50_enabled: true,
        threshold_25_enabled: true,
        threshold_5_enabled: true,
        threshold_0_enabled: true
      });

      // Build usage data from transactions for charting
      buildUsageData(data.transactions || [], data.balance || null);

    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleTopupClick = () => {
    window.open('https://klys.ai/texo/#Texo-Plans', '_blank');
  };

  const handleExportTransactions = () => {
    setExportModalVisible(true);
  };

  const handleExportConfirm = async () => {
    try {
      const [startDate, endDate] = exportDateRange;
      
      // Filter transactions by date range if specified
      let filteredTransactions = transactions;
      
      if (startDate && endDate) {
        filteredTransactions = transactions.filter(txn => {
          const txnDate = dayjs(txn.created_at);
          return txnDate.isAfter(startDate.startOf('day')) && txnDate.isBefore(endDate.endOf('day'));
        });
      }

      if (filteredTransactions.length === 0) {
        message.warning('No transactions found in the selected date range');
        return;
      }

      // Convert transactions to CSV format
      const headers = ['Date', 'Type', 'Credits', 'Balance After', 'Description', 'Transaction ID'];
      const csvRows = [headers.join(',')];

      filteredTransactions.forEach(txn => {
        const row = [
          dayjs(txn.created_at).format('YYYY-MM-DD HH:mm:ss'),
          txn.type || 'N/A',
          txn.credits || 0,
          txn.balance_after || 0,
          `"${(txn.description || '').replace(/"/g, '""')}"`, // Escape quotes
          txn.transaction_id || 'N/A'
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const dateRangeStr = startDate && endDate 
        ? `${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}`
        : dayjs().format('YYYY-MM-DD');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${dateRangeStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`${filteredTransactions.length} transactions exported successfully`);
      setExportModalVisible(false);
      setExportDateRange([null, null]);
    } catch (error) {
      message.error('Failed to export transactions');
    }
  };

  const buildUsageData = (txns: Transaction[], currentBalance: CreditBalance) => {
    // Early return if no transactions
    if (!txns || txns.length === 0) {
      setUsageData([]);
      return;
    }
    
    // Group transactions by date
    const dailyData: { [key: string]: { consumed: number; purchased: number } } = {};
    
    txns.forEach((txn) => {
      const date = dayjs(txn.created_at).format('MMM DD');
      if (!dailyData[date]) {
        dailyData[date] = { consumed: 0, purchased: 0 };
      }
      
      // Handle consumption/deduction (credits will be negative)
      if (txn.type === 'consumption' || txn.type === 'deduction') {
        dailyData[date].consumed += Math.abs(txn.credits);
      } else if (txn.type === 'purchase') {
        dailyData[date].purchased += Math.abs(txn.credits);
      }
    });

    // Create chart data for last 30 days
    const chartData: UsageDataPoint[] = [];
    
    // Calculate what the balance was 30 days ago
    let balanceAtStart = currentBalance.current_balance;
    for (let i = 29; i >= 0; i--) {
      const date = dayjs().subtract(i, 'day').format('MMM DD');
      const dayData = dailyData[date] || { consumed: 0, purchased: 0 };
      // Work backwards: remove purchases, add back consumption
      balanceAtStart = balanceAtStart - dayData.purchased + dayData.consumed;
    }
    
    // Now build forward from the start
    let runningBalance = Math.max(0, balanceAtStart);
    for (let i = 29; i >= 0; i--) {
      const date = dayjs().subtract(i, 'day').format('MMM DD');
      const dayData = dailyData[date] || { consumed: 0, purchased: 0 };
      
      chartData.push({
        date,
        consumed: dayData.consumed,
        purchased: dayData.purchased,
        balance: runningBalance
      });
      
      // Apply day's transactions: add purchases, subtract consumption
      runningBalance = Math.max(0, runningBalance + dayData.purchased - dayData.consumed);
    }

    // Add forecast for next 30 days based on average daily consumption
    const avgDailyConsumption = currentBalance.credits_consumed_month > 0 
      ? currentBalance.credits_consumed_month / 30 
      : 0;
    
    runningBalance = currentBalance.current_balance;
    
    for (let i = 1; i <= forecastDays; i++) {
      const date = dayjs().add(i, 'day').format('MMM DD');
      runningBalance = Math.max(0, runningBalance - avgDailyConsumption);
      
      chartData.push({
        date,
        consumed: avgDailyConsumption,
        purchased: 0,
        balance: runningBalance
      });
    }

    setUsageData(chartData);
  };

  const calculateForecast = () => {
    if (!balance) return { daysRemaining: 0, depletionDate: null, avgDailyUsage: 0 };
    
    const avgDailyUsage = balance.credits_consumed_month / 30 || 0;
    const daysRemaining = avgDailyUsage > 0 ? Math.floor(balance.current_balance / avgDailyUsage) : 999;
    const depletionDate = avgDailyUsage > 0 ? dayjs().add(daysRemaining, 'day') : null;
    
    return { daysRemaining, depletionDate, avgDailyUsage };
  };

  const forecast = calculateForecast();

  const handleUpdateAlertPreferences = async (updatedPrefs: AlertPreferences) => {
    try {
      setSavingPreferences(true);

      await billingAPI.updateAlertPreferences(updatedPrefs);

      setAlertPreferences(updatedPrefs);
      message.success('Alert preferences updated successfully');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setSavingPreferences(false);
    }
  };

  const getBalanceStatus = (): { status: 'success' | 'warning' | 'error'; color: string; icon: React.ReactNode } => {
    if (!balance) {
      return { status: 'warning', color: '#faad14', icon: <WarningOutlined /> };
    }

    const total = balance.total_purchased || balance.plan?.default_credits || 1;
    const percentage = (balance.current_balance / total) * 100;

    if (percentage > 50) {
      return { status: 'success', color: '#52c41a', icon: <CheckCircleOutlined /> };
    } else if (percentage > 25) {
      return { status: 'warning', color: '#faad14', icon: <WarningOutlined /> };
    } else if (percentage > 5) {
      return { status: 'warning', color: '#fa8c16', icon: <WarningOutlined /> };
    } else {
      return { status: 'error', color: '#ff4d4f', icon: <WarningOutlined /> };
    }
  };

  const balanceStatus = getBalanceStatus();

  const transactionColumns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY HH:mm'),
      sorter: (a: Transaction, b: Transaction) => 
        dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: 'descend' as const
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        if (!type) return <Tag color="default">UNKNOWN</Tag>;
        
        const colors: Record<string, string> = {
          purchase: 'green',
          consumption: 'orange',
          deduction: 'orange',
          adjustment: 'blue'
        };
        const displayName = type === 'consumption' ? 'USAGE' : type.toUpperCase();
        return <Tag color={colors[type] || 'default'}>{displayName}</Tag>;
      },
      filters: [
        { text: 'Purchase', value: 'purchase' },
        { text: 'Usage', value: 'consumption' },
        { text: 'Deduction', value: 'deduction' },
        { text: 'Adjustment', value: 'adjustment' }
      ],
      onFilter: (value: any, record: Transaction) => record.type === value
    },
    {
      title: 'Credits',
      dataIndex: 'credits',
      key: 'credits',
      render: (credits: number, record: Transaction) => {
        if (credits === undefined || credits === null) {
          return <Text type="secondary">N/A</Text>;
        }
        
        const isPositive = record.type === 'purchase' || (record.type === 'adjustment' && credits > 0);
        const displayCredits = (record.type === 'consumption' || record.type === 'deduction') ? Math.abs(credits) : credits;
        return (
          <Text strong style={{ color: isPositive ? '#52c41a' : '#ff4d4f' }}>
            {isPositive ? '+' : '-'}{displayCredits.toLocaleString()}
          </Text>
        );
      }
    },
    {
      title: 'Balance After',
      dataIndex: 'balance_after',
      key: 'balance_after',
      render: (balance: number) => balance?.toLocaleString() || 'N/A'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    }
  ];

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f8faff' }}>
        <AppHeader />
        <Content style={{ padding: '24px 48px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Loading billing data...</Text>
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f8faff' }}>
      <AppHeader />
      <Content style={{ padding: '24px 48px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%', marginBottom: 24 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(-1)}
              style={{ marginBottom: 8 }}
            >
              Back
            </Button>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Title level={2} style={{ marginBottom: 8 }}>Billing & Credits</Title>
                <Paragraph type="secondary">
                  Manage your credit balance, view transaction history, and configure alerts
                </Paragraph>
              </div>
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={handleTopupClick}
                style={{ marginTop: 4 }}
              >
                Top-Up Now
              </Button>
            </div>
          </Space>

          <Tabs defaultActiveKey="overview">
            {/* Overview Tab */}
            <TabPane tab="Overview" key="overview">
              {/* Balance Alert */}
              {balance && balance.thresholds_triggered && Array.isArray(balance.thresholds_triggered) && balance.thresholds_triggered.length > 0 && (
                <Alert
                  type={balanceStatus.status}
                  icon={balanceStatus.icon}
                  message="Low Credit Balance Alert"
                  description={`Your credit balance has fallen below ${Math.max(...balance.thresholds_triggered)}%. Consider topping up soon.`}
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              {/* Top-Up Credits */}
              <Card title="Top-Up Credits" style={{ marginBottom: 24 }}>
                <Paragraph>
                  Running low on credits? Purchase additional credits to continue using Texo OCR services.
                </Paragraph>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    message="External Purchase"
                    description="You will be redirected to our secure payment portal to complete your purchase."
                    type="info"
                    showIcon
                  />
                  <Button
                    type="primary"
                    size="large"
                    icon={<LinkOutlined />}
                    block
                    onClick={handleTopupClick}
                  >
                    Top-Up Now
                  </Button>
                  {balance?.last_topup_date && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Last top-up: {dayjs(balance.last_topup_date).format('MMM DD, YYYY')}
                    </Text>
                  )}
                </Space>
              </Card>

              {/* Credit Forecast Card */}
              <Card 
                title={
                  <Space>
                    <LineChartOutlined />
                    <span>Credit Forecast & Usage Projection</span>
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Days Remaining"
                      value={forecast.daysRemaining}
                      suffix="days"
                      prefix={<CalendarOutlined />}
                      valueStyle={{ color: forecast.daysRemaining < 7 ? '#ff4d4f' : forecast.daysRemaining < 15 ? '#fa8c16' : '#52c41a' }}
                    />
                    {forecast.depletionDate && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Est. depletion: {forecast.depletionDate.format('MMM DD, YYYY')}
                      </Text>
                    )}
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Avg. Daily Usage"
                      value={forecast.avgDailyUsage.toFixed(1)}
                      suffix="credits/day"
                      prefix={<RiseOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Monthly Consumption"
                      value={balance?.credits_consumed_month || 0}
                      suffix="credits"
                      prefix={<ThunderboltOutlined />}
                    />
                  </Col>
                </Row>

                {/* Usage and Balance Chart */}
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>30-Day History & {forecastDays}-Day Forecast</Title>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={usageData}>
                      <defs>
                        <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorConsumed" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ff4d4f" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ff4d4f" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis />
                      <RechartsTooltip 
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.96)', borderRadius: 8 }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="balance" 
                        stroke="#1890ff" 
                        fillOpacity={1} 
                        fill="url(#colorBalance)"
                        name="Balance"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="consumed" 
                        stroke="#ff4d4f" 
                        fillOpacity={1} 
                        fill="url(#colorConsumed)"
                        name="Daily Usage"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  
                  <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f0f2f5', borderRadius: 8 }}>
                    <Space split={<Divider type="vertical" />}>
                      <Text type="secondary">
                        <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#1890ff', marginRight: 8, borderRadius: 2 }} />
                        Historical & Projected Balance
                      </Text>
                      <Text type="secondary">
                        <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#ff4d4f', marginRight: 8, borderRadius: 2 }} />
                        Daily Consumption
                      </Text>
                    </Space>
                  </div>
                </div>

                {/* Burn Rate Analysis */}
                <Divider />
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Card size="small" title="Consumption Breakdown">
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text>Today</Text>
                          <Text strong>{balance?.credits_consumed_today || 0} credits</Text>
                        </div>
                        <Progress 
                          percent={balance ? (balance.credits_consumed_today / (balance.credits_consumed_month / 30)) * 100 : 0} 
                          strokeColor="#52c41a"
                          showInfo={false}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text>This Month</Text>
                          <Text strong>{balance?.credits_consumed_month || 0} credits</Text>
                        </div>
                        <Progress 
                          percent={balance && balance.plan ? (balance.credits_consumed_month / balance.plan.default_credits) * 100 : 0} 
                          strokeColor="#1890ff"
                          showInfo={false}
                        />
                      </Space>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" title="Recommended Action">
                      {forecast.daysRemaining < 7 ? (
                        <Alert
                          message="Critical: Top-up needed"
                          description="Your credits will run out within a week. Consider purchasing more credits immediately."
                          type="error"
                          showIcon
                          icon={<WarningOutlined />}
                        />
                      ) : forecast.daysRemaining < 15 ? (
                        <Alert
                          message="Warning: Low balance"
                          description="Plan a credit top-up soon to avoid service interruption."
                          type="warning"
                          showIcon
                        />
                      ) : (
                        <Alert
                          message="Healthy balance"
                          description="Your credit balance is sufficient for current usage patterns."
                          type="success"
                          showIcon
                        />
                      )}
                    </Card>
                  </Col>
                </Row>
              </Card>

          {/* Credit Balance Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Current Balance"
                  value={balance?.current_balance || 0}
                  prefix={<ThunderboltOutlined style={{ color: balanceStatus.color }} />}
                  suffix="credits"
                  valueStyle={{ color: balanceStatus.color }}
                />
                {balance && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {((balance.current_balance / (balance.total_purchased || 1)) * 100).toFixed(1)}% remaining
                  </Text>
                )}
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Purchased"
                  value={balance?.total_purchased || 0}
                  prefix={<ShoppingCartOutlined />}
                  suffix="credits"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Consumed"
                  value={balance?.total_consumed || 0}
                  prefix={<DollarOutlined />}
                  suffix="credits"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Used Today"
                  value={balance?.credits_consumed_today || 0}
                  prefix={<ClockCircleOutlined />}
                  suffix="credits"
                />
              </Card>
            </Col>
          </Row>

          {/* Current Plan */}
          <Card title="Current Plan" extra={plan && <Tag color="blue">{plan.plan_code}</Tag>}>
            {plan ? (
              <>
                <Title level={4}>{plan.name}</Title>
                <Paragraph type="secondary">{plan.description}</Paragraph>
                <Divider />
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Credits Included: </Text>
                    <Text>{balance?.total_purchased?.toLocaleString() || 0}</Text>
                  </div>
                  <div>
                    <Text strong>Price: </Text>
                    <Text>{plan.currency}</Text>
                  </div>
                  {plan.features && plan.features.length > 0 && (
                    <>
                      <Divider />
                      <Text strong>Features:</Text>
                      <ul style={{ paddingLeft: 20, marginTop: 8 }}>
                        {plan.features.map((feature, idx) => (
                          <li key={idx}><Text>{feature}</Text></li>
                        ))}
                      </ul>
                    </>
                  )}
                </Space>
              </>
            ) : (
              <Alert message="No plan assigned" type="info" />
            )}
          </Card>
        </TabPane>

        {/* Transactions Tab */}
        <TabPane tab="Transaction History" key="transactions">
          <Card
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportTransactions}
                disabled={!transactions || transactions.length === 0}
              >
                Export CSV
              </Button>
            }
          >
            <Table
              columns={transactionColumns}
              dataSource={transactions}
              rowKey="transaction_id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} transactions`
              }}
            />
          </Card>
        </TabPane>

        {/* Alert Configuration Tab */}
        <TabPane tab="Alert Configuration" key="alerts">
          <Row gutter={16}>
            {/* Webhook Status */}
            <Col xs={24} lg={12}>
              <Card title="Webhook Alerts" extra={webhookEnabled ? <Tag color="green">Enabled</Tag> : <Tag color="red">Disabled</Tag>}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    message="Webhook Configuration"
                    description={
                      webhookEnabled
                        ? 'Webhook alerts are enabled globally by your administrator. You will receive POST notifications when credit thresholds are reached.'
                        : 'Webhook alerts are currently disabled. Contact your administrator to enable webhook notifications.'
                    }
                    type={webhookEnabled ? 'success' : 'warning'}
                    showIcon
                  />
                  {webhookEnabled && (
                    <div style={{ marginTop: 16 }}>
                      <Text strong>Active Thresholds:</Text>
                      <div style={{ marginTop: 8 }}>
                        <Tag color="orange">50%</Tag>
                        <Tag color="orange">25%</Tag>
                        <Tag color="red">5%</Tag>
                        <Tag color="red">0%</Tag>
                      </div>
                      <Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
                        Webhooks are triggered once per threshold level until credits are topped up.
                      </Paragraph>
                    </div>
                  )}
                </Space>
              </Card>
            </Col>

            {/* Email Alert Preferences */}
            <Col xs={24} lg={12}>
              <Card title="Email Alert Preferences" extra={<MailOutlined />}>
                <Form layout="vertical">
                  <Form.Item label="Enable Email Alerts">
                    <Switch
                      checked={alertPreferences.email_alerts_enabled}
                      onChange={(checked) => {
                        const updated = { ...alertPreferences, email_alerts_enabled: checked };
                        handleUpdateAlertPreferences(updated);
                      }}
                      loading={savingPreferences}
                    />
                  </Form.Item>

                  <Divider />

                  <Paragraph type="secondary">
                    Configure which threshold alerts you want to receive via email:
                  </Paragraph>

                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item label="75% Threshold Alert">
                      <Switch
                        checked={alertPreferences.threshold_75_enabled}
                        disabled={!alertPreferences.email_alerts_enabled}
                        onChange={(checked) => {
                          const updated = { ...alertPreferences, threshold_75_enabled: checked };
                          handleUpdateAlertPreferences(updated);
                        }}
                        loading={savingPreferences}
                      />
                    </Form.Item>

                    <Form.Item label="50% Threshold Alert">
                      <Switch
                        checked={alertPreferences.threshold_50_enabled}
                        disabled={!alertPreferences.email_alerts_enabled}
                        onChange={(checked) => {
                          const updated = { ...alertPreferences, threshold_50_enabled: checked };
                          handleUpdateAlertPreferences(updated);
                        }}
                        loading={savingPreferences}
                      />
                    </Form.Item>

                    <Form.Item label="25% Threshold Alert">
                      <Switch
                        checked={alertPreferences.threshold_25_enabled}
                        disabled={!alertPreferences.email_alerts_enabled}
                        onChange={(checked) => {
                          const updated = { ...alertPreferences, threshold_25_enabled: checked };
                          handleUpdateAlertPreferences(updated);
                        }}
                        loading={savingPreferences}
                      />
                    </Form.Item>

                    <Form.Item label="5% Critical Alert">
                      <Switch
                        checked={alertPreferences.threshold_5_enabled}
                        disabled={!alertPreferences.email_alerts_enabled}
                        onChange={(checked) => {
                          const updated = { ...alertPreferences, threshold_5_enabled: checked };
                          handleUpdateAlertPreferences(updated);
                        }}
                        loading={savingPreferences}
                      />
                    </Form.Item>

                    <Form.Item label="0% Depleted Alert">
                      <Switch
                        checked={alertPreferences.threshold_0_enabled}
                        disabled={!alertPreferences.email_alerts_enabled}
                        onChange={(checked) => {
                          const updated = { ...alertPreferences, threshold_0_enabled: checked };
                          handleUpdateAlertPreferences(updated);
                        }}
                        loading={savingPreferences}
                      />
                    </Form.Item>
                  </Space>

                  <Alert
                    message="Email Delivery"
                    description="Alerts will be sent to your registered email address. Each threshold triggers only once until credits are topped up."
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                </Form>
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>
      
      {/* Export Modal */}
      <Modal
        title="Export Transactions"
        open={exportModalVisible}
        onOk={handleExportConfirm}
        onCancel={() => {
          setExportModalVisible(false);
          setExportDateRange([null, null]);
        }}
        okText="Export"
        cancelText="Cancel"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Select a date range to export transactions (optional):</Text>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            value={exportDateRange}
            onChange={(dates) => setExportDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
          />
          <Alert
            message="Leave date range empty to export all transactions"
            type="info"
            showIcon
          />
          <Text type="secondary">
            Total transactions available: {transactions ? transactions.length : 0}
          </Text>
        </Space>
      </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default BillingPage;
